import type { SetStatusV3Status } from "@redotech/redo-model/advanced-flow/rules/actions";
import { SetStatusSnoozePeriod } from "@redotech/redo-model/advanced-flow/rules/actions";
import type { SetStatusV3Action as SetStatusV3ActionType } from "@redotech/redo-model/advanced-flow/rules/schemas";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useMemo } from "react";

const statusOptions: RedoListItem<SetStatusV3Status>[] = [
  { id: "OPEN", type: "text", text: "Open", value: "OPEN" },
  { id: "CLOSED", type: "text", text: "Closed", value: "CLOSED" },
  { id: "SNOOZED", type: "text", text: "Snoozed", value: "SNOOZED" },
  {
    id: "IN_PROGRESS",
    type: "text",
    text: "In progress",
    value: "IN_PROGRESS",
  },
  { id: "ARCHIVED", type: "text", text: "Archived", value: "ARCHIVED" },
  { id: "DELETED", type: "text", text: "Deleted", value: "DELETED" },
];

const snoozePeriodOptions: RedoListItem<SetStatusSnoozePeriod>[] = [
  {
    id: SetStatusSnoozePeriod.TODAY,
    type: "text",
    text: "Later the same day",
    value: SetStatusSnoozePeriod.TODAY,
  },
  {
    id: SetStatusSnoozePeriod.TOMORROW,
    type: "text",
    text: "The next day",
    value: SetStatusSnoozePeriod.TOMORROW,
  },
  {
    id: SetStatusSnoozePeriod.WEEKEND,
    type: "text",
    text: "The next Saturday",
    value: SetStatusSnoozePeriod.WEEKEND,
  },
  {
    id: SetStatusSnoozePeriod.NEXT_WEEK,
    type: "text",
    text: "The next Monday",
    value: SetStatusSnoozePeriod.NEXT_WEEK,
  },
];

export const SetStatusV3Action = memo(function SetStatusV3Action({
  action,
  onChange,
  editableFields,
}: {
  action: SetStatusV3ActionType;
  onChange: (updated: SetStatusV3ActionType) => void;
  /**
   * When omitted, all fields are editable. When provided, only fields
   * in the set are editable.
   */
  editableFields?: ReadonlySet<string>;
}) {
  const statusEditable =
    editableFields === undefined || editableFields.has("status");
  const snoozePeriodEditable =
    editableFields === undefined || editableFields.has("snoozePeriod");

  const selectedStatus = useMemo(
    () => statusOptions.find((opt) => opt.value === action.status),
    [action.status],
  );

  const selectedSnoozePeriod = useMemo(
    () => snoozePeriodOptions.find((opt) => opt.value === action.snoozePeriod),
    [action.snoozePeriod],
  );

  return (
    <Flex dir="column" gap="md" w="full">
      <RedoSingleSelectDropdownInput<SetStatusV3Status>
        disabled={!statusEditable}
        fitToAnchor
        label="Status"
        options={statusOptions}
        optionSelected={({ item }) => {
          if (!statusEditable) return;
          onChange({ ...action, status: item.value });
        }}
        selectedItem={selectedStatus}
      />
      {action.status === "SNOOZED" && (
        <RedoSingleSelectDropdownInput<SetStatusSnoozePeriod>
          disabled={!snoozePeriodEditable}
          fitToAnchor
          label="Until"
          options={snoozePeriodOptions}
          optionSelected={({ item }) => {
            if (!snoozePeriodEditable) return;
            onChange({ ...action, snoozePeriod: item.value });
          }}
          selectedItem={selectedSnoozePeriod}
        />
      )}
    </Flex>
  );
});
